import React, { useState, useRef } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix } from '../../../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { useAppData } from '../../../../core/contexts/AppDataContext';

// Simple print function replacement
const useReactToPrint = ({ content, documentTitle, onAfterPrint }) => {
  return () => {
    if (content && content()) {
      const printContent = content().innerHTML;
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>${documentTitle || 'Document'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              table { border-collapse: collapse; width: 100%; }
              th, td { border: 1px solid #000; padding: 8px; text-align: left; }
              .text-center { text-align: center; }
              .text-right { text-align: right; }
              .font-bold { font-weight: bold; }
              .font-semibold { font-weight: 600; }
              .bg-gray-200 { background-color: #f3f4f6; }
              .bg-gray-100 { background-color: #f9fafb; }
              .bg-gray-50 { background-color: #f9fafb; }
              @media print { body { margin: 0; } }
            </style>
          </head>
          <body>${printContent}</body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      if (onAfterPrint) onAfterPrint();
    }
  };
};

/**
 * Payslip component — Saudi payroll (GOSI, SANED, no income tax).
 */
const BulletinSalaire = ({ employeeData, salaryData, month, year }) => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.salaires.pages.bulletinSalaire.${key}`, opts);
  const { settings } = useAppData();
  const [showPreview, setShowPreview] = useState(true);
  const bulletinRef = useRef();
  
  // Fonction pour imprimer le bulletin
  const handlePrint = useReactToPrint({
    content: () => bulletinRef.current,
    documentTitle: `Bulletin_de_paie_${employeeData?.nom || ''}_${employeeData?.prenom || ''}_${month}_${year}`,
    onAfterPrint: () => console.log('Print complete')
  });
  
  // Données par défaut pour le test
  const defaultEmployeeData = {
    matricule: '001',
    nom: 'AL-HARBI',
    prenom: 'MOHAMMED',
    dateNaissance: '14/04/1990',
    adresse: 'حي النخيل، الرياض',
    fonction: 'محاسب',
    dateEntree: '01/01/2020',
    numeroSS: '1234567890',
    numeroIBAN: 'SA0380000000608010167519',
    situationFamiliale: 'Marié',
    enfants: 2,
    nationality: 'SA',
  };

  const defaultSalaryData = {
    periode: {
      du: '01/07/2024',
      au: '31/07/2024',
      joursOuvrables: 22,
      joursTravailles: 22,
      joursAbsences: 0,
      heuresTravaillees: 176
    },

    gains: [
      { code: 'SB', libelle: 'الراتب الأساسي', base: '', taux: '', nombreOuMontant: 15000, imposable: false, cotisable: true },
      { code: 'IH', libelle: 'بدل السكن', base: 15000, taux: 25, nombreOuMontant: 3750, imposable: false, cotisable: true },
      { code: 'IT', libelle: 'بدل النقل', base: '', taux: '', nombreOuMontant: 1500, imposable: false, cotisable: false },
      { code: 'IM', libelle: 'بدل الاتصالات', base: '', taux: '', nombreOuMontant: 500, imposable: false, cotisable: false },
      { code: 'PR', libelle: 'مكافأة الأداء', base: 15000, taux: 10, nombreOuMontant: 1500, imposable: false, cotisable: true }
    ],

    retenues: [
      { code: 'GOSI', libelle: 'اشتراك GOSI — الموظف', base: 18750, taux: 12, nombreOuMontant: 2250, imposable: false, cotisable: false },
      { code: 'SANED', libelle: 'اشتراك SANED — الموظف', base: 18750, taux: 1, nombreOuMontant: 187.5, imposable: false, cotisable: false }
    ],

    cotisationsEmployeur: [
      { libelle: 'اشتراك GOSI — صاحب العمل', taux: 10, montant: 1875 },
      { libelle: 'اشتراك SANED — صاحب العمل', taux: 1, montant: 187.5 },
      { libelle: 'مخصص نهاية الخدمة (EOSB)', taux: 0, montant: 625 }
    ],

    totaux: {
      brut: 22250,
      cotisable: 18750,
      imposable: 22250,
      retenues: 2437.5,
      net: 19812.5
    }
  };
  
  // Utiliser les données fournies ou les données par défaut
  const employee = employeeData || defaultEmployeeData;
  const salary = salaryData || defaultSalaryData;
  
  // Formatage des montants pour l'affichage
  const formatMontant = (montant) => {
    if (montant === undefined || montant === null) return '';
    return formatBrandMoney(montant, false);
  };
  
  return (
    <div className="mb-8">
      {/* Boutons d'action */}
      <div className="flex justify-end space-x-4 mb-6">
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center space-x-2"
        >
          <span>{showPreview ? p('hide_preview') : p('show_preview')}</span>
        </button>
        
        <button
          onClick={handlePrint}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center space-x-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z" clipRule="evenodd" />
          </svg>
          <span>{p('btn_print')}</span>
        </button>
      </div>
      
      {/* Aperçu du bulletin */}
      {showPreview && (
        <div className="overflow-auto">
          {/* Bulletin de paie imprimable */}
          <div 
            ref={bulletinRef}
            className="bg-white text-black w-[21cm] mx-auto p-8 shadow-lg"
            style={{ minHeight: '29.7cm' }}
          >
            {/* En-tête du bulletin */}
            <div className="flex justify-between border-b-2 border-gray-800 pb-4 mb-6">
              <div>
                <h1 className="text-xl font-bold uppercase">{settings?.company?.name || p('default_company_name')}</h1>
                <p className="text-sm">{settings?.company?.address || p('default_company_address')}</p>
                <p className="text-sm">SIRET: {settings?.company?.registrationNumber || p('default_registration')}</p>
              </div>
              <div className="text-right">
                <h2 className="text-lg font-bold">{p('payslip_title')}</h2>
                <p className="text-sm">{p('period_label')} {salary.periode?.du || '01/07/2023'} {p('period_to')} {salary.periode?.au || '31/07/2023'}</p>
              </div>
            </div>
            
            {/* Informations salarié */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="space-y-1">
                <p><span className="font-semibold">{p('lbl_matricule')}</span> {employee.matricule}</p>
                <p><span className="font-semibold">{p('lbl_full_name')}</span> {employee.nom} {employee.prenom}</p>
                <p><span className="font-semibold">{p('lbl_birth_date')}</span> {employee.dateNaissance}</p>
                <p><span className="font-semibold">{p('lbl_address')}</span> {employee.adresse}</p>
                <p><span className="font-semibold">{p('lbl_job')}</span> {employee.fonction}</p>
                <p><span className="font-semibold">{p('lbl_hire_date')}</span> {employee.dateEntree}</p>
              </div>
              <div className="space-y-1">
                <p><span className="font-semibold">{p('lbl_ssn')}</span> {employee.numeroSS}</p>
                <p><span className="font-semibold">{p('lbl_ccp')}</span> {employee.numeroCCP}</p>
                <p><span className="font-semibold">{p('lbl_marital')}</span> {employee.situationFamiliale}</p>
                <p><span className="font-semibold">{p('lbl_children')}</span> {employee.enfants}</p>
                <p><span className="font-semibold">{p('lbl_grid')}</span> {employee.grille}</p>
                <p><span className="font-semibold">{p('lbl_payment_method')}</span> {p('payment_bank_transfer')}</p>
              </div>
            </div>
            
            {/* Temps de travail */}
            <div className="flex justify-between mb-6 bg-gray-100 p-2">
              <div className="flex-1 text-center">
                <p className="font-semibold">{p('lbl_working_days')}</p>
                <p>{salary.periode?.joursOuvrables || 22}</p>
              </div>
              <div className="flex-1 text-center">
                <p className="font-semibold">{p('lbl_days_worked')}</p>
                <p>{salary.periode?.joursTravailles || 22}</p>
              </div>
              <div className="flex-1 text-center">
                <p className="font-semibold">{p('lbl_absences')}</p>
                <p>{salary.periode?.joursAbsences || 0}</p>
              </div>
              <div className="flex-1 text-center">
                <p className="font-semibold">{p('lbl_hours_worked')}</p>
                <p>{salary.periode?.heuresTravaillees || 176}</p>
              </div>
            </div>
            
            {/* Tableau des rubriques */}
            <table className="w-full border border-gray-300 mb-6">
              <thead className="bg-gray-200">
                <tr>
                  <th className="border border-gray-300 px-2 py-1 text-sm text-center">{p('col_code')}</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm text-left">{p('col_designation')}</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm text-right">{p('col_base')}</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm text-right">{p('col_rate')}</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm text-right">{p('col_amount')}</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm text-center">{p('col_gain')}</th>
                  <th className="border border-gray-300 px-2 py-1 text-sm text-center">{p('col_deduction')}</th>
                </tr>
              </thead>
              <tbody>
                {/* Gains */}
                {salary.gains.map((gain, index) => (
                  <tr key={`gain-${index}`} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-center">{gain.code}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm">{gain.libelle}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-right">{gain.base ? formatMontant(gain.base) : ''}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-right">{gain.taux ? gain.taux : ''}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-right">{formatMontant(gain.nombreOuMontant)}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-right font-semibold">{formatMontant(gain.nombreOuMontant)}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm"></td>
                  </tr>
                ))}
                
                {/* Retenues */}
                {salary.retenues.map((retenue, index) => (
                  <tr key={`retenue-${index}`} className={(salary.gains.length + index) % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-center">{retenue.code}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm">{retenue.libelle}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-right">{retenue.base ? formatMontant(retenue.base) : ''}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-right">{retenue.taux ? retenue.taux : ''}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-right">{formatMontant(retenue.nombreOuMontant)}</td>
                    <td className="border border-gray-300 px-2 py-1 text-sm"></td>
                    <td className="border border-gray-300 px-2 py-1 text-sm text-right font-semibold">{formatMontant(retenue.nombreOuMontant)}</td>
                  </tr>
                ))}
                
                {/* Ligne de totaux */}
                <tr className="bg-gray-200 font-bold">
                  <td className="border border-gray-300 px-2 py-1 text-sm text-center" colSpan={5}>{p('totals_label')}</td>
                  <td className="border border-gray-300 px-2 py-1 text-sm text-right">{formatMontant(salary.totaux.brut)}</td>
                  <td className="border border-gray-300 px-2 py-1 text-sm text-right">{formatMontant(salary.totaux.retenues)}</td>
                </tr>
              </tbody>
            </table>
            
            {/* Récapitulatif et net à payer */}
            <div className="flex flex-wrap mb-6">
              <div className="w-1/2 pr-2">
                <div className="border border-gray-300 mb-4">
                  <div className="bg-gray-200 px-3 py-1 border-b border-gray-300 font-semibold text-sm">
                    {p('summary_title')}
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="px-3 py-1 border-b border-gray-300">{p('summary_gross')}</td>
                        <td className="px-3 py-1 border-b border-gray-300 text-right">{formatBrandMoney(salary.totaux.brut)}</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-1 border-b border-gray-300">{p('summary_cotisable')}</td>
                        <td className="px-3 py-1 border-b border-gray-300 text-right">{formatBrandMoney(salary.totaux.cotisable)}</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-1 border-b border-gray-300">{p('summary_imposable')}</td>
                        <td className="px-3 py-1 border-b border-gray-300 text-right">{formatBrandMoney(salary.totaux.imposable)}</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-1">{p('summary_deductions')}</td>
                        <td className="px-3 py-1 text-right">{formatBrandMoney(salary.totaux.retenues)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                {/* Cotisations employeur (pour information) */}
                <div className="border border-gray-300">
                  <div className="bg-gray-200 px-3 py-1 border-b border-gray-300 font-semibold text-sm">
                    {p('employer_contrib_title')}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-3 py-1 border-b border-gray-300 text-left">{p('col_designation')}</th>
                        <th className="px-3 py-1 border-b border-gray-300 text-right">{p('col_rate')}</th>
                        <th className="px-3 py-1 border-b border-gray-300 text-right">{p('col_amount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salary.cotisationsEmployeur.map((cotisation, index) => (
                        <tr key={`cotisation-${index}`}>
                          <td className="px-3 py-1 border-b border-gray-300">{cotisation.libelle}</td>
                          <td className="px-3 py-1 border-b border-gray-300 text-right">{cotisation.taux}</td>
                          <td className="px-3 py-1 border-b border-gray-300 text-right">{formatBrandMoney(cotisation.montant)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="w-1/2 pl-2">
                {/* Net à payer */}
                <div className="border border-gray-300 mb-4">
                  <div className="bg-gray-800 text-white px-3 py-2 text-center font-bold text-lg">
                    {p('net_pay_title')}
                  </div>
                  <div className="bg-gray-100 px-3 py-6 text-center font-bold text-2xl">
                    {formatBrandMoney(salary.totaux.net)}
                  </div>
                  <div className="px-3 py-2 text-center text-sm">
                    {p('paid_by', { method: p('payment_bank_transfer'), date: '30/07/2023' })}
                  </div>
                </div>
                
                {/* Cumuls annuels */}
                <div className="border border-gray-300">
                  <div className="bg-gray-200 px-3 py-1 border-b border-gray-300 font-semibold text-sm">
                    {p('annual_totals_title')}
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      <tr>
                        <td className="px-3 py-1 border-b border-gray-300">{p('annual_taxable')}</td>
                        <td className="px-3 py-1 border-b border-gray-300 text-right">{formatBrandMoney(salary.totaux.imposable * 7)}</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-1 border-b border-gray-300">{p('annual_irg')}</td>
                        <td className="px-3 py-1 border-b border-gray-300 text-right">{formatBrandMoney(salary.retenues.find(r => r.code === 'R660')?.nombreOuMontant * 7 || 0)}</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-1">{p('annual_net')}</td>
                        <td className="px-3 py-1 text-right">{formatBrandMoney(salary.totaux.net * 7)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            {/* Pied de page */}
            <div className="border-t border-gray-300 pt-4 text-xs text-center">
              <p>{p('footer_legal')}</p>
              <p>{p('footer_keep')}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulletinSalaire; 

